import React from 'react';
import { observer } from 'mobx-react';
import { computed, observable } from 'mobx';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import { AppUI, PrivacySettingsState, PrivacySettingsUI } from '@seedlang/state';
import styled from '@emotion/styled';
import { pixify } from '@seedlang/utils/src';
import { flexCenter } from '@seedlang/style_mixins';

const VideoWrapper = styled.div`
  position: relative;
  width: ${AppUI.layout.isMobile ? '360px' : '540px'};
  height: ${AppUI.layout.isMobile ? '205px' : '300px'};
  margin: 0 auto;
`;

const FloatingVideo = styled.div`
  position: ${props => props.isFloating ? 'fixed' : 'relative'};
  bottom: ${props => props.isFloating ? '20px' : 'auto'};
  left: ${props => props.isFloating ? '20px' : 'auto'};
  z-index: ${props => props.isFloating ? '1000' : 'auto'};
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  transition-property: position, bottom, left, z-index, box-shadow, border-radius;
  box-shadow: ${props => props.isFloating ? '0 4px 12px rgba(0, 0, 0, 0.15)' : 'none'};
  border-radius: ${props => props.isFloating ? '8px' : '0'};
  will-change: transform, opacity;
  transform: ${props => props.isFloating ? 'scale(0.65)' : 'none'};
  transform-origin: bottom left;
  backface-visibility: hidden;
  width: ${AppUI.layout.isMobile ? '360px' : '540px'};
  height: ${AppUI.layout.isMobile ? '205px' : '300px'};
`;

const PrivacyNotice = styled.div`
  width: ${props => pixify(props.width)};
  height: ${props => pixify(props.height)};
  padding: 1em;
  text-align: center;
  font-size: 14px;
  line-height: 1.5;
  text-wrap: balance;
  display: grid;
  place-content: center;
  position: relative;
  .background {
    position: absolute;
    inset: 0;
    z-index: 1;
    background-color: #DDD;
    ${({ placeholderImageUrl }) => placeholderImageUrl && `
      background-image: url('${placeholderImageUrl}');
      background-size: cover;
      filter: blur(5px);
    `}
  }
  .notice-text {
    z-index: 2;
    padding: 1em;
    background: rgba(255,255,255,0.9);
    border-radius: 0.5em;
    .title {
      font-size: 120%;
      font-weight: bold;
    }
  }
`;

const PrivacyButton = styled.div`
  display: inline-block;
  background: ${props => props.background};
  color: ${props => props.color};
  width: ${props => props.width};
  margin: ${props => props.margin};
  padding: 5px;
  font-weight: bold;
  cursor: pointer;
  border-radius: 10px;
`;

const CloseButton = styled.div`
  position: absolute;
  top: -10px;
  right: -10px;
  width: 40px;
  height: 40px;
  font-size: 24px;
  background: #000;
  border-radius: 50%;
  ${flexCenter()}
  cursor: pointer;
  color: #fff;
  z-index: 2;
`;

@observer
class YoutubeVideo extends React.Component {
  @observable isOutOfView = false;
  @observable floatingDisabledByUser = false;

  constructor(props) {
    super(props);
    this.wrapperRef = React.createRef();
  }

  @computed get isFloating() {
    return !AppUI.layout.isMobile && this.isOutOfView && !this.floatingDisabledByUser && this.props.allowFloating;
  }

  componentDidMount() {
    if (AppUI.layout.isMobile || !this.props.allowFloating) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const newIsOutOfView = entry.intersectionRatio < 0.4;
        if (newIsOutOfView !== this.isOutOfView) {
          this.isOutOfView = newIsOutOfView;
        }
      },
      {
        threshold: [0, 0.4, 0.6, 1],
        rootMargin: '-10% 0px',
      },
    );

    if (this.wrapperRef.current) {
      observer.observe(this.wrapperRef.current);
    }

    return () => observer.disconnect();
  }

  handleCloseFloating = () => {
    this.floatingDisabledByUser = true;
  };

  render() {
    const videoContent = PrivacySettingsState.isYoutubeAllowed ? (
      <div className='youtube-video'>
        <iframe
          src={this.props.url}
          width={AppUI.layout.isMobile ? 360 : 540}
          height={AppUI.layout.isMobile ? 205 : 300}
          allowFullScreen
          frameBorder='0'
          title='Video'
        />
      </div>
    ) : (
      <PrivacyNotice
        className='youtube-video'
        width={AppUI.layout.isMobile ? 360 : 540}
        height={AppUI.layout.isMobile ? 'fit-content' : 300}
        isMobile={AppUI.layout.isMobile}
        placeholderImageUrl={this.props.placeholderImageUrl}
      >
        <div className='background'></div>
        <div className='notice-text'>
          <p className='title'>Opt in to watch YouTube videos</p>
          <p>To respect your privacy, we do not load YouTube videos automatically. YouTube may collect personal data and track your activity if videos are played directly from our site.</p>
          <p>If you would like to watch the video, please click the button below to opt-in and enable the YouTube content.</p>
          <PrivacyButton
            background='#0584a1'
            color='#FFF'
            onClick={PrivacySettingsUI.showModal}
            width={AppUI.layout.isMobile ? '100%' : '200px'}
            margin={AppUI.layout.isMobile ? '5px 0 0 0' : '0 0 0 5px'}
          >
            Edit Privacy Preferences
          </PrivacyButton>
        </div>
      </PrivacyNotice>
    );

    return (
      <VideoWrapper ref={this.wrapperRef}>
        <FloatingVideo isFloating={this.isFloating}>
          {videoContent}
          {this.isFloating && (
            <CloseButton onClick={this.handleCloseFloating}>
              <i className='fa fa-times'></i>
            </CloseButton>
          )}
        </FloatingVideo>
      </VideoWrapper>
    );
  }
}

export default ErrorBoundary(YoutubeVideo);
